import { Link, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown } from "@/components/animate-ui/icons/chevron-down";
import { EllipsisVertical } from "@/components/animate-ui/icons/ellipsis-vertical";
import { PanelTopOpen } from "@/components/animate-ui/icons/panel-top-open";
import { Pause } from "@/components/animate-ui/icons/pause";
import { Plus } from "@/components/animate-ui/icons/plus";
import { TaskConfirmationModal, type TaskFormData } from "@/components/task-confirmation-modal";
import { ConfirmActionDialog } from "@/components/teammate/confirm-action-dialog";
import { CreateTeammateDialog } from "@/components/teammate/create-teammate-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Spinner } from "@/components/ui/spinner";
import {
	useAgents,
	useArchiveAgent,
	useCreateSchedule,
	useCreateTask,
	useDisableAgent,
} from "@/lib/api/hooks/tasks";
import { useAuth } from "@/lib/auth/hooks";
import { useBubbleStream } from "@/lib/streaming/use-bubble-stream";
import { getInitials } from "@/lib/utils/get-initials";

export function NavTeammates() {
	const [isOpen, setIsOpen] = useState(true);
	const { data: agents, isLoading } = useAgents();
	const { accessToken } = useAuth();
	const matchRoute = useMatchRoute();
	const navigate = useNavigate();

	// Task creation modal state
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
	const [taskInstructions, setTaskInstructions] = useState("");
	const emptyIntegrations = useMemo(() => [], []);

	// Create teammate dialog state
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

	// Confirmation dialog state
	const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);
	const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
	const [actionTargetAgentId, setActionTargetAgentId] = useState<number | null>(null);

	// API mutations
	const createTask = useCreateTask();
	const createSchedule = useCreateSchedule();
	const disableAgent = useDisableAgent(actionTargetAgentId || 0);
	const archiveAgent = useArchiveAgent(actionTargetAgentId || 0);

	// Stream handler
	const { start: startExecution, runId, isStreaming } = useBubbleStream();

	// Navigate to run view when runId is available
	useEffect(() => {
		if (runId && isStreaming) {
			navigate({ to: "/runs/$runId", params: { runId: String(runId) } });
		}
	}, [runId, isStreaming, navigate]);

	const handleNewTask = (agentId: number, agentStatus: string) => {
		// Check if agent is enabled
		if (agentStatus !== "enabled") {
			toast.error("Cannot create task for disabled or archived agent");
			return;
		}
		setSelectedAgentId(agentId);
		setTaskInstructions("");
		setIsModalOpen(true);
	};

	const handlePauseAgent = (agentId: number) => {
		setActionTargetAgentId(agentId);
		setIsPauseDialogOpen(true);
	};

	const handleArchiveAgent = (agentId: number) => {
		setActionTargetAgentId(agentId);
		setIsArchiveDialogOpen(true);
	};

	const confirmPause = async () => {
		if (!actionTargetAgentId) return;

		try {
			await disableAgent.mutateAsync();
			setIsPauseDialogOpen(false);
			toast.success("Agent paused successfully");
		} catch (error) {
			console.error("Failed to pause agent:", error);
			toast.error("Failed to pause agent. Please try again.");
		}
	};

	const confirmArchive = async () => {
		if (!actionTargetAgentId) return;

		try {
			await archiveAgent.mutateAsync();
			setIsArchiveDialogOpen(false);
			toast.success("Agent archived successfully");
		} catch (error) {
			console.error("Failed to archive agent:", error);
			toast.error("Failed to archive agent. Please try again.");
		}
	};

	const handleSubmitTask = async (formData: TaskFormData) => {
		if (!selectedAgentId) return;

		try {
			const task = await createTask.mutateAsync({
				agent_instance_id: selectedAgentId,
				name: formData.instructions.slice(0, 100),
				instructions: formData.instructions,
				expected_output: formData.expectedOutput || null,
				goals: formData.goals || null,
				integration_ids: formData.integrations.length > 0 ? formData.integrations : null,
				email_notifications: formData.emailNotifications,
				permission_mode: formData.permissionMode,
			});

			if (formData.schedule === "now") {
				setIsModalOpen(false);
				toast.success("Task created! Starting execution...");
				await startExecution({
					instructions: formData.instructions,
					requestBody: {
						instructions: formData.instructions,
						expected_output: formData.expectedOutput || null,
						goals: formData.goals || null,
						integration_ids: formData.integrations.length > 0 ? formData.integrations : [],
						email_notifications: formData.emailNotifications,
						inbound_email_enabled: formData.inboundEmailEnabled,
						permission_mode: formData.permissionMode,
						schedule: "now",
						timezone: formData.timezone || "UTC",
					},
					accessToken: accessToken || "",
				});
			} else {
				let cronExpression: string | null = null;
				let intervalSeconds: number | null = null;

				if (formData.schedule === "interval") {
					const minutes =
						formData.intervalUnit === "hours"
							? (formData.intervalMinutes || 60) * 60
							: formData.intervalMinutes || 30;
					intervalSeconds = minutes * 60;
				} else {
					const minute = formData.timeMinute || 0;
					const hour = formData.timeHour || 9;

					if (formData.schedule === "daily") {
						cronExpression = `${minute} ${hour} * * *`;
					} else if (formData.schedule === "weekly") {
						const days = (formData.daysOfWeek || [1]).join(",");
						cronExpression = `${minute} ${hour} * * ${days}`;
					} else if (formData.schedule === "monthly") {
						const day = formData.dayOfMonth || 1;
						cronExpression = `${minute} ${hour} ${day} * *`;
					}
				}

				await createSchedule.mutateAsync({
					task_id: task.id,
					agent_instance_id: selectedAgentId,
					cron_expression: cronExpression,
					interval_seconds: intervalSeconds,
					run_date: null,
					timezone: formData.timezone || "UTC",
				});

				setIsModalOpen(false);
				toast.success("Task created and scheduled successfully!");
			}
		} catch (error) {
			console.error("Failed to create task:", error);
			toast.error("Failed to create task. Please try again.");
		}
	};

	return (
		<SidebarGroup className="pb-2 pt-2">
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				{/* Fully clickable header */}
				<CollapsibleTrigger className="flex items-center gap-2 px-2 py-1 w-full hover:bg-sidebar-accent rounded-md transition-colors group">
					<span className="text-sm font-medium truncate flex-1 min-w-0 text-left text-sidebar-foreground">
						Mates
					</span>
					<ChevronDown
						animateOnHover
						className={`size-4 shrink-0 transition-transform ml-auto ${
							isOpen ? "rotate-0" : "-rotate-90"
						}`}
					/>
				</CollapsibleTrigger>

				<CollapsibleContent>
					<SidebarGroupContent>
						<ScrollArea className="max-h-[250px] [&_[data-slot=scroll-area-viewport]]:h-auto [&_[data-slot=scroll-area-viewport]]:max-h-[250px]">
							<SidebarMenu>
								{isLoading ? (
									<div className="px-3 py-1.5 flex justify-center">
										<Spinner className="size-4" />
									</div>
								) : (
									(agents ?? [])
										.filter((agent) => agent.status !== "archived")
										.map((agent) => {
											const isActive = matchRoute({
												to: "/teammates/$id",
												params: { id: agent.id.toString() },
											});

											return (
												<SidebarMenuItem key={agent.id}>
													<SidebarMenuButton
														asChild
														isActive={!!isActive}
														className="py-1 [&>span:last-child]:truncate"
													>
														<Link to="/teammates/$id" params={{ id: agent.id.toString() }}>
															<Avatar className="size-5 shrink-0">
																<AvatarFallback className="bg-primary/10 text-primary text-[10px] font-medium">
																	{getInitials(agent.name)}
																</AvatarFallback>
															</Avatar>
															<span className="flex flex-col flex-1 w-0 overflow-hidden">
																<span className="flex items-center gap-1">
																	<span className="text-xs font-medium truncate">{agent.name}</span>
																	{agent.status === "disabled" && (
																		<Pause
																			animateOnHover
																			className="size-3 text-muted-foreground"
																		/>
																	)}
																</span>
																<span className="text-[11px] text-muted-foreground truncate">
																	{agent.role || "Team Member"}
																</span>
															</span>
														</Link>
													</SidebarMenuButton>

													{/* 3-dot menu */}
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<SidebarMenuAction showOnHover>
																<EllipsisVertical animateOnHover className="size-4" />
																<span className="sr-only">Mate options</span>
															</SidebarMenuAction>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															<DropdownMenuItem
																onClick={() => handleNewTask(agent.id, agent.status)}
															>
																<Plus animateOnHover className="size-4" />
																New Task
															</DropdownMenuItem>
															<DropdownMenuItem onClick={() => handlePauseAgent(agent.id)}>
																<Pause animateOnHover className="size-4" />
																Pause
															</DropdownMenuItem>
															<DropdownMenuItem onClick={() => handleArchiveAgent(agent.id)}>
																<PanelTopOpen animateOnHover className="size-4" />
																Archive
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</SidebarMenuItem>
											);
										})
								)}
								<SidebarMenuItem>
									<SidebarMenuButton
										className="py-1 text-muted-foreground"
										onClick={() => setIsCreateDialogOpen(true)}
									>
										<Plus animateOnHover className="size-4" />
										<span className="text-xs">New Mate</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
							</SidebarMenu>
						</ScrollArea>
					</SidebarGroupContent>
				</CollapsibleContent>
			</Collapsible>

			{/* Create teammate dialog */}
			<CreateTeammateDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

			{/* Task creation modal */}
			<TaskConfirmationModal
				open={isModalOpen}
				onOpenChange={setIsModalOpen}
				instructions={taskInstructions}
				onSubmit={handleSubmitTask}
				isSubmitting={createTask.isPending || createSchedule.isPending}
				defaultIntegrations={emptyIntegrations}
				showInstructionsField={true}
				showPermissionModeSelector={true}
				hideEmailInboxSection={true}
			/>

			{/* Pause confirmation dialog */}
			<ConfirmActionDialog
				open={isPauseDialogOpen}
				onOpenChange={setIsPauseDialogOpen}
				title="Pause"
				description="Are you sure you want to pause this mate? It will not execute any tasks until resumed."
				confirmText="Pause"
				onConfirm={confirmPause}
				isLoading={disableAgent.isPending}
				variant="default"
			/>

			{/* Archive confirmation dialog */}
			<ConfirmActionDialog
				open={isArchiveDialogOpen}
				onOpenChange={setIsArchiveDialogOpen}
				title="Archive"
				description="Are you sure you want to archive this mate? This action cannot be undone and the mate will be permanently removed."
				confirmText="Archive"
				onConfirm={confirmArchive}
				isLoading={archiveAgent.isPending}
				variant="destructive"
			/>
		</SidebarGroup>
	);
}
